import React, { useEffect, useState, useMemo, useCallback } from "react";

import { Transaction } from "@local-types/transaction/tableRow";

import Card from "./Card";
import Calendar from "./Calendar";

import { staticColumnDefs } from "@components/stat/transaction/table/columnDefs";
import TransactionsTable from "@components/stat/transaction/table/TransactionsTable";

import { useAppStore } from "@store/appStateStore";

import "./DashboardCard.css";

const ParticipantDashboardCard: React.FC = () => {
  const { transactions, attendances, directions, user, participant } =
    useAppStore();

  // Определяем participantId текущего пользователя
  const participantId = user?.id ?? participant ?? null;

  const [participantTransactions, setParticipantTransactions] = useState<
    Transaction[]
  >([]);

  useEffect(() => {
    const newParticipantTransactions: Transaction[] =
      transactions.filter((t) => t.participant_id === participantId) || [];
    setParticipantTransactions(newParticipantTransactions);
  }, [transactions, participantId]);

  // Расчет баланса участника (без фильтрации по периоду)
  const calculateBalance = useCallback(() => {
    if (!participantId) return 0;

    // Сумма транзакций
    const totalTransactions = participantTransactions.reduce(
      (sum, t) => sum + t.amount,
      0,
    );

    // Фильтрация посещений по участнику
    const participantAttendances = attendances.filter(
      (a) => a.participant_id === participantId,
    );

    // Сумма посещений
    const totalAttendances = participantAttendances.reduce(
      (sum, a) => sum + a.amount,
      0,
    );

    return totalTransactions - totalAttendances;
  }, [participantTransactions, attendances, participantId]);

  // Подсчет посещенных занятий
  const countAttendances = useCallback(() => {
    if (!participantId) return 0;

    const participantAttendances = attendances.filter(
      (a) => a.participant_id === participantId,
    );

    return participantAttendances.length;
  }, [attendances, participantId]);

  // Получение уникальных дат посещений для календаря с направлением
  const getAttendanceDates = useCallback((): Array<{
    date: string;
    direction: string | number;
  }> => {
    if (!participantId) return [];

    const participantAttendances = attendances.filter(
      (a) => a.participant_id === participantId,
    );

    // Группируем по дате, сохраняем direction_id первого посещения
    const dateMap = new Map<string, string | number>();
    participantAttendances.forEach((attendance) => {
      // attendance.date может быть в формате "2024-12-31" или "2024-12-31T00:00:00.000Z"
      const date = new Date(attendance.date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;
      // Если дата ещё не добавлена, добавляем с direction_id
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, attendance.direction_id);
      }
    });

    return Array.from(dateMap.entries()).map(([date, direction]) => ({
      date,
      direction,
    }));
  }, [attendances, participantId]);

  // Оптимизируем вычисления с помощью useMemo
  const balance = useMemo(() => calculateBalance(), [calculateBalance]);
  const attendanceCount = useMemo(() => countAttendances(), [countAttendances]);
  const attendanceDates = useMemo(
    () => getAttendanceDates(),
    [getAttendanceDates],
  );

  // Функция для получения контента тултипа по дате
  const getTooltipContent = useMemo(
    () =>
      (date: string): React.ReactNode => {
        if (!participantId) return null;

        // Приводим дату к формату YYYY-MM-DD для сравнения
        const targetDate = new Date(date);
        const targetDateStr = targetDate.toISOString().split("T")[0]; // "YYYY-MM-DD"

        // Фильтруем посещения участника за эту дату
        const dayAttendances = attendances.filter((a) => {
          if (a.participant_id !== participantId) return false;
          const attendanceDate = new Date(a.date);
          const attendanceDateStr = attendanceDate.toISOString().split("T")[0];
          return attendanceDateStr === targetDateStr;
        });

        if (dayAttendances.length === 0) {
          return <div>Нет посещений</div>;
        }

        // Группируем по direction_id для суммирования amount
        const grouped: Record<
          number,
          {
            totalAmount: number;
            count: number;
            directionName: string | null;
            directionId: number | null;
          }
        > = {};
        dayAttendances.forEach((att) => {
          const dir = directions.find((d) => d.id === att.direction_id);
          const dirName = dir ? dir.name : null;
          const dirId = dir ? dir.id : null;
          if (!grouped[att.direction_id]) {
            grouped[att.direction_id] = {
              totalAmount: 0,
              count: 0,
              directionName: dirName,
              directionId: dirId,
            };
          }
          grouped[att.direction_id].totalAmount += att.amount;
          grouped[att.direction_id].count += 1;
        });

        return (
          <div className="attendance-tooltip">
            <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
              Посещения за {targetDate.toLocaleDateString("ru-RU")}
            </div>
            {Object.entries(grouped).map(([dirId, data]) => {
              return (
                <div key={dirId} style={{ marginBottom: "2px" }}>
                  {data.directionName && <span>{data.directionName} </span>}
                  <span>
                    {data.count} занятий, сумма {data.totalAmount.toFixed(2)}{" "}
                    руб.
                  </span>
                </div>
              );
            })}
            <div style={{ marginTop: "6px", fontSize: "12px", color: "#666" }}>
              Всего посещений: {dayAttendances.length}
            </div>
          </div>
        );
      },
    [attendances, directions, participantId],
  );

  // Пустые обработчики для календаря (так как выбор дат не требуется)
  const handleDateSelect = (date: string) => {
    // Ничего не делаем
  };

  const handleDateRemove = (date: string) => {
    // Ничего не делаем
  };

  return (
    <>
      <Card title="Дашборд участника" width="half">
        <div className="dashboard-content">
          <div className="dashboard-stats">
            <div className="stat-item">
              <h4>Баланс</h4>
              <p
                className={`balance-amount ${balance >= 0 ? "positive" : "negative"}`}
              >
                {balance.toFixed(2)} руб.
              </p>
            </div>
            <div className="stat-item">
              <h4>Посещено занятий</h4>
              <p className="attendance-count">{attendanceCount}</p>
            </div>
          </div>
        </div>
      </Card>
      <Card>
        <Calendar
          selectedDates={attendanceDates}
          onDateSelect={handleDateSelect}
          onDateRemove={handleDateRemove}
          dateSectionSow={false}
          getTooltipContent={getTooltipContent}
        />
      </Card>
      <Card>
        <TransactionsTable
          customColumnDefs={staticColumnDefs}
          customTransactions={participantTransactions}
        />
      </Card>
    </>
  );
};

export default ParticipantDashboardCard;
